import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HeroMediaManager } from '@/components/admin/HeroMediaManager';
import { StockImageManager } from '@/components/admin/StockImageManager';
import { SiteImageUpload } from '@/components/admin/SiteImageUpload';
import { Image as ImageIcon, FileImage, Sparkles, Building2, ExternalLink, Video } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function AdminImageManagement() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Image Management</h2>
        <p className="text-muted-foreground">
          Manage site-wide images including hero banners, stock photos, and amenity images. All images are automatically optimized with responsive variants.
        </p>
      </div>

      {/* Info Alert */}
      <Alert>
        <ImageIcon className="h-4 w-4" />
        <AlertDescription>
          <strong>Note:</strong> Images uploaded here are automatically optimized and stored in Supabase Storage. 
          For batch processing of multiple images, use the command-line utilities documented in{' '}
          <a 
            href="/docs/development/manual-image-processing.md" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            the manual processing guide
            <ExternalLink className="h-3 w-3" />
          </a>.
        </AlertDescription>
      </Alert>

      {/* Tabs for different image types */}
      <Tabs defaultValue="hero" className="space-y-4">
        <TabsList>
          <TabsTrigger value="hero" className="flex items-center gap-2">
            <Video className="h-4 w-4" />
            Hero Media
          </TabsTrigger>
          <TabsTrigger value="stock" className="flex items-center gap-2">
            <FileImage className="h-4 w-4" />
            Stock Images
          </TabsTrigger>
          <TabsTrigger value="amenity" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Amenity Images
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="space-y-4">
          <HeroMediaManager />
        </TabsContent>

        <TabsContent value="stock" className="space-y-4">
          <StockImageManager />
        </TabsContent>

        <TabsContent value="amenity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Amenity Images</CardTitle>
              <CardDescription>
                Upload and manage community amenity images. These images are displayed in the amenities section and should be optimized for web display (4:3 aspect ratio).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SiteImageUpload
                imageType="amenity"
                existingImageUrls={[]}
                onImageUpdated={() => {}}
                onImagesUpdated={(urls) => {
                  console.log('Amenity images updated:', urls);
                  // TODO: Store amenity image URLs in database or update communityAmenities.ts
                }}
                label="Upload Amenity Images"
                description="Upload multiple amenity images. They will be optimized and stored in Supabase Storage."
                maxImages={50}
              />
              <Alert className="mt-4">
                <AlertDescription>
                  <strong>Note:</strong> After uploading, you'll need to update the image URLs in{' '}
                  <code className="bg-muted px-1 py-0.5 rounded text-xs">src/data/communityAmenities.ts</code>{' '}
                  to use the new optimized images. Consider using the batch processing utility for initial setup.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Reference Card */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Reference</CardTitle>
          <CardDescription>
            Common image processing workflows and utilities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Single Image Processing</h4>
              <code className="block text-xs bg-muted p-2 rounded">
                node scripts/process-images.js {'<input> <output> <name> <type>'}
              </code>
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Batch Processing</h4>
              <code className="block text-xs bg-muted p-2 rounded">
                node scripts/batch-process-images.js {'<inputDir> <outputDir> <type>'}
              </code>
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Workflow Utility</h4>
              <code className="block text-xs bg-muted p-2 rounded">
                node scripts/process-site-images.js {'<workflow> [options]'}
              </code>
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Supported Types</h4>
              <code className="block text-xs bg-muted p-2 rounded">
                hero, stock, amenity, exterior_render, gallery, floor_plan, thumbnail
              </code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

